import './ActualVsPredicted.scss';
import { useCallback, useEffect, useMemo, useRef, useState, FC, Fragment } from 'react';
import cn from 'classnames';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Area, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import {
  dateStringToUtcTimestamp,
  toBpmDateString
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE
} from '@Components/Charts/ChartConstants';
import {
  KeyShape,
  PayloadItem,
  TooltipPassthroughProps,
  ColorKey
} from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { headerFormatter, useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import { useBuildMonthlyXAxis } from '@Components/Charts/MonthlyXAxis/MonthlyXAxis';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Neutral300, Neutral500, Neutral600 } from '@src/utils/colors';
import { dateToMddyy } from '@src/utils/date-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import {
  actualKey,
  AXES,
  buildChartData,
  buildChartSeries,
  ChartAxis,
  ChartModel,
  ChartSeries,
  dynamicKey,
  getDefaultActive,
  HistoricalRevenueDataPoint,
  PREDICTED_KEY,
  PREDICTED_LINE_STYLE,
  seriesOfAxis,
  stackedKey,
  toggleLegendKey
} from '../utils/modelOverviewUtils';

interface ActualVsPredictedProps {
  data: HistoricalRevenueDataPoint[];
  dateGrouping: string;
  legendValue: string;
  syncId?: number;
  tooltipFormatter?: (value: number) => string;
  yAxisFormatter?: (value: number) => string;
  secondaryYAxisFormatter?: (value: number) => string;
  yAxisStaticWidth?: number;
  setChartYAxisDynamicWidth?: (width: number) => void;
  height?: string;
  width?: string;
  shouldSortTooltipItems?: boolean;
  canUseMonthlyTicks?: boolean;
  tooltipProps?: TooltipPassthroughProps;
  models: ChartModel[];
  // When provided, overrides the internally computed domain so this chart shares an
  // identical numeric time domain with another stacked chart.
  xAxisDomain?: [number, number];
  // Explicit x-axis tick timestamps. When provided, they override recharts' auto-generated
  // ticks so this chart can share an identical tick set with another stacked chart.
  xAxisTicks?: number[];
}

const RIGHT_MARGIN = 0;
const TOP_MARGIN = 20;
const ACTUAL_PREFIX = 'actual:';

interface LegendState {
  enabledKey: string;
  active: Record<string, boolean>;
}

export const ActualVsPredicted: FC<ActualVsPredictedProps> = ({
  data,
  dateGrouping,
  legendValue: _legendValue,
  syncId,
  tooltipFormatter,
  yAxisFormatter,
  secondaryYAxisFormatter,
  yAxisStaticWidth,
  setChartYAxisDynamicWidth,
  height: _height = '100%',
  width: _width = '100%',
  shouldSortTooltipItems = true,
  canUseMonthlyTicks = false,
  tooltipProps: _tooltipProps = {},
  models,
  xAxisDomain: xAxisDomainProp,
  xAxisTicks
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { fiscal_cal: fiscalCal } = useCompanyInfo();

  const series = useMemo(() => buildChartSeries(models), [models]);
  const enabledKey = models
    .filter(model => model.enabled)
    .map(model => model.id)
    .sort()
    .join(',');
  const enabledIds = useMemo(() => (enabledKey ? enabledKey.split(',') : []), [enabledKey]);
  const enabledSeries = useMemo(
    () => series.filter(s => enabledIds.includes(s.id)),
    [series, enabledIds]
  );
  const axisEnabled = (axis: ChartAxis): ChartSeries[] => seriesOfAxis(enabledSeries, axis);

  const defaultActive = useMemo(() => getDefaultActive(series, enabledIds), [series, enabledIds]);
  const [legendState, setLegendState] = useState<LegendState | null>(null);
  const activeKeyMap = legendState?.enabledKey === enabledKey ? legendState.active : defaultActive;

  const groupedData = useMemo(
    () => buildChartData(data, dateGrouping, series, enabledIds, activeKeyMap),
    [data, dateGrouping, series, enabledIds, activeKeyMap]
  );

  const axisLabel = (axis: ChartAxis): string => {
    const parts = axisEnabled(axis).filter(s => activeKeyMap[s.id]);
    return (parts.length > 0 ? parts : axisEnabled(axis)).map(s => s.label).join(' + ');
  };

  const labelMap = Object.fromEntries(series.map(s => [actualKey(s.id), s.label]));

  const { yAxisWidth, xAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDependency: groupedData,
    yAxisStaticWidth
  });

  useEffect(() => {
    if (setChartYAxisDynamicWidth) {
      setChartYAxisDynamicWidth(yAxisWidth);
    }
  }, [setChartYAxisDynamicWidth, yAxisWidth]);

  const computedXAxisDomain = useMemo(() => {
    if (!data || data.length === 0) {
      return undefined;
    }

    const allDates = data.map(item => item.date).filter(Boolean);
    if (allDates.length === 0) {
      return undefined;
    }

    const timestamps = allDates.map(date => dateStringToUtcTimestamp(toBpmDateString(date)));
    const earliestTimestamp = Math.min(...timestamps);
    const latestTimestamp = Math.max(...timestamps);

    return [earliestTimestamp, latestTimestamp] as [number, number];
  }, [data]);

  // A domain supplied by the parent (shared source of truth) wins over the internally
  // derived one, so this chart and a stacked sibling share an identical x-axis domain.
  const xAxisDomain = xAxisDomainProp ?? computedXAxisDomain;

  const dataWithTimestamps = useMemo(
    () =>
      groupedData.map(row => ({
        ...row,
        timestamp: dateStringToUtcTimestamp(toBpmDateString(row.date))
      })),
    [groupedData]
  );

  const { shouldRenderFallback: shouldRenderFallbackAxis, component: monthlyXAxis } =
    useBuildMonthlyXAxis({
      data: dataWithTimestamps,
      yAxisWidth: yAxisStaticWidth || yAxisWidth,
      axisWidth: xAxisWidth,
      domain: xAxisDomain,
      overrideTicks: xAxisTicks
    });
  const shouldRenderMonthlyTimestamps = canUseMonthlyTicks && !shouldRenderFallbackAxis;

  const customTickStyle = { ...TICK_STYLE, fill: Neutral600 };

  const defaultXAxis = xAxisDomain ? (
    <XAxis
      dataKey='timestamp'
      type='number'
      scale='time'
      domain={[xAxisDomain[0], xAxisDomain[1]]}
      ticks={xAxisTicks}
      allowDataOverflow
      tick={customTickStyle}
      tickFormatter={val => {
        const date = new Date(val);
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        return dateToMddyy(dateStr);
      }}
    />
  ) : (
    <XAxis dataKey='date' tick={customTickStyle} tickFormatter={val => dateToMddyy(val)} />
  );

  const xAxisComponent = shouldRenderMonthlyTimestamps ? monthlyXAxis : defaultXAxis;

  const handleLegendItemClick = useCallback(
    (key: string) =>
      setLegendState(prev => ({
        enabledKey,
        active: toggleLegendKey(
          prev?.enabledKey === enabledKey ? prev.active : defaultActive,
          key,
          series,
          enabledIds
        )
      })),
    [enabledKey, defaultActive, series, enabledIds]
  );

  const renderAxisSeries = (axis: ChartAxis) => {
    const yAxisId = axis === 'right' ? 'right' : undefined;
    const { color, opacity } = PREDICTED_LINE_STYLE[axis];
    const areas = [...axisEnabled(axis)]
      .reverse()
      .filter(s => activeKeyMap[s.id])
      .map(s => (
        <Area
          key={s.id}
          dataKey={stackedKey(s.id)}
          name={actualKey(s.id)}
          fill={s.color}
          fillOpacity={opacity}
          isAnimationActive={false}
          stroke={s.color}
          strokeOpacity={opacity}
          type='monotone'
          yAxisId={yAxisId}
        />
      ));
    const showLine =
      axisEnabled(axis).length > 0 &&
      activeKeyMap[PREDICTED_KEY[axis]] &&
      (axis === 'right' || axisEnabled(axis).some(s => activeKeyMap[s.id]));
    return showLine
      ? [
          ...areas,
          <Line
            key={`line-${axis}`}
            dataKey={dynamicKey(axis)}
            dot={false}
            isAnimationActive={false}
            stroke={color}
            strokeOpacity={opacity}
            strokeWidth={3}
            type='monotone'
            yAxisId={yAxisId}
          />
        ]
      : areas;
  };

  return (
    <div className='hroas-actual-vs-predicted-wrapper'>
      <div className='hroas-actual-vs-predicted-graph' ref={containerRef}>
        <AutoSizer
          renderProp={({ width, height }) => (
            <ComposedChart
              data={dataWithTimestamps}
              height={height ?? 0}
              margin={{ top: TOP_MARGIN, right: RIGHT_MARGIN, left: 0, bottom: 0 }}
              syncId={syncId}
              ref={setChartRef}
              width={width ?? 0}
            >
              {xAxisComponent}
              <YAxis
                tick={customTickStyle}
                tickFormatter={yAxisFormatter}
                tickLine={false}
                axisLine={false}
                width={yAxisWidth}
                domain={[0, 'auto']}
              />
              {axisEnabled('right').length > 0 && (
                <YAxis
                  yAxisId='right'
                  orientation='right'
                  tick={customTickStyle}
                  tickFormatter={secondaryYAxisFormatter || yAxisFormatter}
                  tickLine={false}
                  axisLine={false}
                  width={yAxisWidth}
                  domain={[0, 'auto']}
                />
              )}
              <CartesianGrid
                stroke={CARTESIAN_GRID_STROKE}
                strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                vertical={false}
              />
              {AXES.map(renderAxisSeries)}
              <CartesianGrid
                stroke={Neutral300}
                strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                vertical={false}
              />
              <Tooltip
                // eslint-disable-next-line react/no-unstable-nested-components -- recharts render prop closes over parent state/props (labelMap, axisLabel, dateGrouping, fiscalCal, tooltipFormatter, shouldSortTooltipItems); hoisting would require threading all of them through
                content={({ label, payload }) => {
                  if (!payload || !label || payload.length === 0) {
                    return null;
                  }
                  const date = payload[0].payload?.date;
                  const formattedLabel = headerFormatter(dateGrouping, date, fiscalCal);

                  const actualItems: PayloadItem[] = [];
                  const predictedItems: PayloadItem[] = [];

                  payload.forEach(item => {
                    const itemName = String(item.name ?? '');
                    const isActual = itemName.startsWith(ACTUAL_PREFIX);
                    const itemAxis = AXES.find(axis => dynamicKey(axis) === itemName);
                    const color = item.stroke || item.fill || '';
                    const keyShape: KeyShape = isActual ? 'square' : 'line';
                    // Actual areas render the stacked total, so read the individual value from the
                    // raw payload field named after the series.
                    const rawValue = isActual
                      ? ((item.payload as Record<string, number | null>)[itemName] ?? 0)
                      : Number(item.value);
                    const payloadItem: PayloadItem = {
                      label: itemAxis ? axisLabel(itemAxis) : labelMap[itemName] || itemName,
                      value: tooltipFormatter ? tooltipFormatter(rawValue) : rawValue,
                      color,
                      keyShape
                    };

                    if (isActual) {
                      actualItems.push(payloadItem);
                    } else {
                      predictedItems.push(payloadItem);
                    }
                  });
                  if (shouldSortTooltipItems) {
                    actualItems.sort(
                      (a, b) => -Math.abs(Number(a.value)) + Math.abs(Number(b.value))
                    );
                    predictedItems.sort(
                      (a, b) => -Math.abs(Number(a.value)) + Math.abs(Number(b.value))
                    );
                  }

                  return (
                    <div className='hroas-actual-vs-predicted-tooltip'>
                      <div className='hroas-actual-vs-predicted-tooltip-container'>
                        <div className='tooltip-header'>{formattedLabel}</div>
                        <div className='item-list'>
                          {actualItems.length > 0 && (
                            <>
                              <div className='section-header'>Actual Revenue:</div>
                              {actualItems.map((item, i) => {
                                const { label: rowLabel, value, color, keyShape } = item;
                                if (!value) {
                                  return <Fragment key={i} />;
                                }
                                return (
                                  <div className='item-row' key={i}>
                                    <div className='label-container'>
                                      <ColorKey shape={keyShape ?? 'square'} color={color} />
                                      <div className='label'>{rowLabel}</div>
                                    </div>
                                    <div className='value'>{value}</div>
                                  </div>
                                );
                              })}
                            </>
                          )}
                          {predictedItems.length > 0 && (
                            <>
                              <div className='section-header'>Predicted Revenue:</div>
                              {predictedItems.map((item, i) => {
                                const { label: rowLabel, value, color, keyShape } = item;
                                if (!value) {
                                  return <Fragment key={i} />;
                                }
                                return (
                                  <div className='item-row' key={i}>
                                    <div className='label-container'>
                                      <ColorKey shape={keyShape ?? 'line'} color={color} />
                                      <div className='label'>{rowLabel}</div>
                                    </div>
                                    <div className='value'>{value}</div>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }}
                cursor={{ stroke: Neutral500, width: '1px' }}
                isAnimationActive={false}
              />
            </ComposedChart>
          )}
        />
      </div>
      <div className='hroas-right-of-chart'>
        <div className='historical-revenue-legend'>
          <div className='legend-section'>
            <div className='legend-section-title'>Actual Revenue</div>
            {enabledSeries.map(s => (
              <InteractiveDiv
                key={s.id}
                className={cn('legend-item', { disabled: !activeKeyMap[s.id] })}
                onClick={() => handleLegendItemClick(s.id)}
              >
                <div className='legend-square' style={{ backgroundColor: s.color }} />
                <span className='legend-label'>{s.label}</span>
              </InteractiveDiv>
            ))}
          </div>
          <div className='legend-section'>
            <div className='legend-section-title'>Predicted Revenue</div>
            {AXES.filter(
              axis =>
                axisEnabled(axis).length > 0 &&
                (axis === 'right' || axisEnabled(axis).some(s => activeKeyMap[s.id]))
            ).map(axis => (
              <InteractiveDiv
                key={axis}
                className={cn('legend-item', { disabled: !activeKeyMap[PREDICTED_KEY[axis]] })}
                onClick={() => handleLegendItemClick(PREDICTED_KEY[axis])}
              >
                <div
                  className='legend-line'
                  style={{ backgroundColor: PREDICTED_LINE_STYLE[axis].color }}
                />
                <span className='legend-label'>{axisLabel(axis)}</span>
              </InteractiveDiv>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
